import React from 'react';
import {useTranslation} from 'react-i18next';

import {Label} from '@edna/components/FormField';
import {Stack} from '@edna/components/primitives';

import {EPermission, usePermission} from 'src/containers/User';

import {CardPaymentsActions} from './CardPaymentsActions';
import {StornoActions} from './StornoActions';
import {
  CARD_PAYMENTS_CORRECTION_TYPES,
  EPaymentCorrectionType,
  STORNO_CORRECTION_TYPES,
} from './definitions';
import * as S from './style';

export const PaymentsTab = React.memo(() => {
  const {t} = useTranslation();

  const hasCardPaymentsActions = usePermission(EPermission.PERMISSION_PAYKEEPER_CHANGE);
  const hasStornoActions = usePermission(EPermission.PERMISSION_TENANT_TRANSACT_STORNED);

  const [correctionType, setCorrectionType] = React.useState<EPaymentCorrectionType>(
    hasStornoActions ? EPaymentCorrectionType.STORNO : EPaymentCorrectionType.CARD_PAYMENTS,
  );

  const options = React.useMemo(() => {
    const allowedTypes: EPaymentCorrectionType[] = [];

    if (hasStornoActions) {
      allowedTypes.push(...STORNO_CORRECTION_TYPES);
    }

    if (hasCardPaymentsActions) {
      allowedTypes.push(...CARD_PAYMENTS_CORRECTION_TYPES);
    }

    return allowedTypes.map((type) => ({
      label: t(`Corrections:payments.type.${type}`),
      value: type,
    }));
  }, [hasCardPaymentsActions, hasStornoActions, t]);

  return (
    <Stack size="4" isFullWidth>
      <Stack size="1">
        <Label id="correctionType">{t('Corrections:payments.selectType')}</Label>
        <S.Select
          isLabelledBy
          id="correctionType"
          value={correctionType}
          onChange={setCorrectionType}
          isDisplayEmpty={false}
          options={options}
        />
      </Stack>
      {STORNO_CORRECTION_TYPES.includes(correctionType) && (
        <StornoActions correctionType={correctionType} />
      )}
      {CARD_PAYMENTS_CORRECTION_TYPES.includes(correctionType) && (
        <CardPaymentsActions correctionType={correctionType} />
      )}
    </Stack>
  );
});

PaymentsTab.displayName = 'PaymentsTab';
